<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import WorkforceAPI from 'dashboard/api/workforce';
import WorkforceButton from '../components/WorkforceButton.vue';
import WorkforceModal from '../components/WorkforceModal.vue';

const { t } = useI18n();
const store = useStore();

const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');

const rules = ref([]);
const loading = ref(false);
const saving = ref(false);

const filters = reactive({
  userId: 'all',
  enforcedOnly: false,
});

const defaultRuleForm = () => ({
  id: null,
  user_id: filters.userId !== 'all' ? Number(filters.userId) : null,
  max_active_conversations: null,
  min_active_conversations: 0,
  cooldown_seconds: 0,
  allowed_inbox_ids: [],
  enforced: true,
});

const ruleModal = reactive({
  open: false,
  isNew: true,
  form: defaultRuleForm(),
});

const loadRules = async () => {
  loading.value = true;
  try {
    const response = await WorkforceAPI.getAssignmentRules();
    rules.value = response.data || [];
  } catch (error) {
    useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.FETCH_ERROR'));
  } finally {
    loading.value = false;
  }
};

const filteredRules = computed(() => {
  return rules.value.filter(rule => {
    const matchesAgent =
      filters.userId === 'all' ||
      String(rule.user_id) === String(filters.userId);
    const matchesEnforced = !filters.enforcedOnly || rule.enforced;
    return matchesAgent && matchesEnforced;
  });
});

const agentName = userId => {
  const agent = agents.value.find(item => String(item.id) === String(userId));
  return agent ? agent.name : t('WORKFORCE.LABELS.UNKNOWN_AGENT');
};

const resetRuleForm = () => {
  Object.assign(ruleModal.form, defaultRuleForm());
  ruleModal.isNew = true;
};

const openCreateForm = userId => {
  ruleModal.open = true;
  ruleModal.isNew = true;
  Object.assign(ruleModal.form, defaultRuleForm(), {
    user_id:
      userId || (filters.userId !== 'all' ? Number(filters.userId) : null),
  });
};

const openEditForm = rule => {
  ruleModal.open = true;
  ruleModal.isNew = false;
  Object.assign(ruleModal.form, {
    id: rule.id,
    user_id: Number(rule.user_id),
    max_active_conversations: rule.max_active_conversations ?? null,
    min_active_conversations: rule.min_active_conversations ?? 0,
    cooldown_seconds: rule.cooldown_seconds ?? 0,
    allowed_inbox_ids: Array.isArray(rule.allowed_inbox_ids)
      ? rule.allowed_inbox_ids.map(String)
      : [],
    enforced: Boolean(rule.enforced),
  });
};

const closeRuleModal = () => {
  ruleModal.open = false;
  resetRuleForm();
};

const submitForm = async () => {
  if (!ruleModal.form.user_id) {
    useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.SELECT_AGENT'));
    return;
  }

  const payload = {
    user_id: Number(ruleModal.form.user_id),
    max_active_conversations: ruleModal.form.max_active_conversations
      ? Number(ruleModal.form.max_active_conversations)
      : null,
    min_active_conversations: ruleModal.form.min_active_conversations
      ? Number(ruleModal.form.min_active_conversations)
      : null,
    cooldown_seconds: Number(ruleModal.form.cooldown_seconds || 0),
    enforced: Boolean(ruleModal.form.enforced),
    allowed_inbox_ids: ruleModal.form.allowed_inbox_ids
      .filter(Boolean)
      .map(Number),
  };

  saving.value = true;
  try {
    if (ruleModal.isNew) {
      await WorkforceAPI.createAssignmentRule(payload);
      useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.CREATED'));
    } else {
      await WorkforceAPI.updateAssignmentRule(ruleModal.form.id, payload);
      useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.UPDATED'));
    }
    await loadRules();
    closeRuleModal();
  } catch (error) {
    useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

const removeRule = async rule => {
  if (!rule?.id) return;
  saving.value = true;
  try {
    await WorkforceAPI.deleteAssignmentRule(rule.id);
    useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.DELETED'));
    await loadRules();
  } catch (error) {
    useAlert(t('WORKFORCE.ASSIGNMENT.TOASTS.DELETE_ERROR'));
  } finally {
    saving.value = false;
  }
};

watch(
  () => filters.userId,
  value => {
    if (
      value !== 'all' &&
      !agents.value.find(agent => String(agent.id) === String(value))
    ) {
      filters.userId = 'all';
    }
  }
);

onMounted(async () => {
  if (!agents.value.length) {
    await store.dispatch('agents/get');
  }
  if (!inboxes.value.length) {
    await store.dispatch('inboxes/get');
  }
  await loadRules();
});
</script>

<template>
  <section class="flex flex-col gap-5 px-8 py-5 lg:px-10">
    <!-- Filter + Actions bar -->
    <div
      class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-n-weak bg-n-solid-2 px-5 py-3 shadow-sm"
    >
      <div class="flex flex-wrap items-center gap-3">
        <label
          class="flex items-center gap-2 text-xs uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FILTERS.AGENT') }}
          <select
            v-model="filters.userId"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-1.5 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          >
            <option value="all">{{ t('WORKFORCE.FILTERS.ALL_AGENTS') }}</option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
        <label
          class="flex cursor-pointer items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-3 px-3 py-1.5 text-xs text-n-slate-10 transition hover:bg-n-solid-1"
        >
          <input
            v-model="filters.enforcedOnly"
            type="checkbox"
            class="size-3.5 rounded border-n-weak text-black focus:ring-n-strong/30 dark:text-white dark:focus:ring-white/30"
          />
          {{ t('WORKFORCE.ASSIGNMENT.FILTERS.ENFORCED_ONLY') }}
        </label>
      </div>
      <WorkforceButton size="sm" icon="i-lucide-plus" @click="openCreateForm()">
        {{ t('WORKFORCE.ASSIGNMENT.ACTIONS.ADD_RULE') }}
      </WorkforceButton>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
      <div
        class="flex items-center justify-between border-b border-n-weak px-5 py-3"
      >
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ t('WORKFORCE.ASSIGNMENT.TABLE.TITLE') }}
        </h3>
        <span class="text-xs text-n-slate-10">
          {{
            t('WORKFORCE.ASSIGNMENT.TABLE.COUNT', {
              count: filteredRules.length,
            })
          }}
        </span>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-n-weak text-sm">
          <thead
            class="bg-n-solid-2 text-left text-xs uppercase tracking-wide text-n-slate-10"
          >
            <tr>
              <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.AGENT') }}</th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.MAX') }}
              </th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.MIN') }}
              </th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.COOLDOWN') }}
              </th>
              <th class="px-5 py-3">
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.INBOXES') }}
              </th>
              <th class="px-5 py-3">{{ t('WORKFORCE.TABLE.STATUS') }}</th>
              <th class="px-5 py-3 text-right">
                {{ t('WORKFORCE.TABLE.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak/70 bg-n-solid-3 text-n-slate-12">
            <tr v-if="loading">
              <td
                colspan="7"
                class="px-5 py-6 text-center text-sm text-n-slate-10"
              >
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.LOADING') }}
              </td>
            </tr>
            <tr v-else-if="!filteredRules.length">
              <td
                colspan="7"
                class="px-5 py-6 text-center text-sm text-n-slate-10"
              >
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.EMPTY') }}
              </td>
            </tr>
            <tr v-for="rule in filteredRules" :key="rule.id">
              <td class="px-5 py-3 font-medium">
                {{ agentName(rule.user_id) }}
              </td>
              <td class="px-5 py-3">
                {{
                  rule.max_active_conversations ??
                  t('WORKFORCE.ASSIGNMENT.TABLE.UNLIMITED')
                }}
              </td>
              <td class="px-5 py-3">
                {{ rule.min_active_conversations ?? 0 }}
              </td>
              <td class="px-5 py-3">
                {{ rule.cooldown_seconds }}
                {{ t('WORKFORCE.ASSIGNMENT.TABLE.SECONDS') }}
              </td>
              <td class="px-5 py-3 max-w-sm">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="inboxId in rule.allowed_inbox_ids"
                    :key="inboxId"
                    class="rounded-full bg-n-solid-2 px-2 py-1 text-xs text-n-slate-11"
                  >
                    {{
                      inboxes.value.find(inbox => inbox.id === inboxId)?.name ||
                      t('WORKFORCE.ASSIGNMENT.TABLE.ALL_INBOXES')
                    }}
                  </span>
                  <span
                    v-if="
                      !rule.allowed_inbox_ids || !rule.allowed_inbox_ids.length
                    "
                    class="text-xs text-n-slate-10"
                  >
                    {{ t('WORKFORCE.ASSIGNMENT.TABLE.ALL_INBOXES') }}
                  </span>
                </div>
              </td>
              <td class="px-5 py-3">
                <span
                  class="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs"
                  :class="
                    rule.enforced
                      ? 'bg-teal-3/40 text-teal-11'
                      : 'bg-n-weak/40 text-n-slate-10'
                  "
                >
                  <span
                    class="size-2 rounded-full"
                    :class="[rule.enforced ? 'bg-teal-10' : 'bg-n-slate-8']"
                  />
                  {{
                    rule.enforced
                      ? t('WORKFORCE.ASSIGNMENT.STATUS.ENFORCED')
                      : t('WORKFORCE.ASSIGNMENT.STATUS.DISABLED')
                  }}
                </span>
              </td>
              <td class="px-5 py-3 text-right">
                <div class="flex justify-end gap-2">
                  <WorkforceButton
                    size="xs"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    @click="openEditForm(rule)"
                  >
                    {{ t('WORKFORCE.ACTIONS.EDIT') }}
                  </WorkforceButton>
                  <WorkforceButton
                    size="xs"
                    variant="ghost"
                    icon="i-lucide-trash"
                    :is-loading="saving"
                    @click="removeRule(rule)"
                  >
                    {{ t('WORKFORCE.ACTIONS.DELETE') }}
                  </WorkforceButton>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <WorkforceModal
      v-model="ruleModal.open"
      :title="
        ruleModal.isNew
          ? t('WORKFORCE.ASSIGNMENT.FORM.CREATE_TITLE')
          : t('WORKFORCE.ASSIGNMENT.FORM.EDIT_TITLE')
      "
      :confirm-label="t('WORKFORCE.ACTIONS.SAVE')"
      :cancel-label="t('WORKFORCE.ACTIONS.CANCEL')"
      :loading="saving"
      width="xl"
      @confirm="submitForm"
      @cancel="closeRuleModal"
    >
      <div class="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <label
          class="grid gap-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.FIELDS.AGENT') }}
          <select
            v-model.number="ruleModal.form.user_id"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          >
            <option disabled value="">
              {{ t('WORKFORCE.FIELDS.AGENT_PLACEHOLDER') }}
            </option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
        <label
          class="grid gap-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ASSIGNMENT.FORM.MAX') }}
          <input
            v-model.number="ruleModal.form.max_active_conversations"
            type="number"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
            :placeholder="t('WORKFORCE.ASSIGNMENT.FORM.UNLIMITED_PLACEHOLDER')"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ASSIGNMENT.FORM.MIN') }}
          <input
            v-model.number="ruleModal.form.min_active_conversations"
            type="number"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-medium uppercase tracking-wide text-n-slate-10"
        >
          {{ t('WORKFORCE.ASSIGNMENT.FORM.COOLDOWN') }}
          <input
            v-model.number="ruleModal.form.cooldown_seconds"
            type="number"
            min="0"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          />
        </label>
        <label
          class="grid gap-1 text-xs font-medium uppercase tracking-wide text-n-slate-10 md:col-span-2 xl:col-span-3"
        >
          {{ t('WORKFORCE.ASSIGNMENT.FORM.INBOXES') }}
          <select
            v-model="ruleModal.form.allowed_inbox_ids"
            multiple
            class="h-32 rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-strong focus:ring-2 focus:ring-n-strong/30 dark:focus:border-white dark:focus:ring-white/30"
          >
            <option value="">
              {{ t('WORKFORCE.ASSIGNMENT.FORM.ALL_INBOX_OPTION') }}
            </option>
            <option
              v-for="inbox in inboxes.value"
              :key="inbox.id"
              :value="String(inbox.id)"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-12">
          <input
            v-model="ruleModal.form.enforced"
            type="checkbox"
            class="size-4 rounded border-n-weak text-black focus:ring-n-strong/30 dark:text-white dark:focus:ring-white/30"
          />
          {{ t('WORKFORCE.ASSIGNMENT.FORM.ENFORCED') }}
        </label>
      </div>
    </WorkforceModal>
  </section>
</template>
